import {
  MaterialRate_Read,
  MaterialRate_Read_Typeahead,
  MaterialRateAssignment_Read_Nested,
  NullableOrderUnitOfMeasure,
  OrderUnitOfMeasure,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';
import { isNil } from 'lodash-es';

import { AccountBasic, AccountTypeahead } from '../useAccount/models';
import { CompanyBasic } from '../useCompany/models';
import { BasicMaterial, MaterialTypeahead } from '../useMaterials/models';
import { ProjectBasic, ProjectTypeahead } from '../useProjects/models';
import { SiteBasic, SiteTypeahead } from '../useSites/models';

export class MaterialRate {
  constructor(
    private _id: string,
    private _buy_price: number | null,
    private _sell_price: number | null,
    private _unit_of_measure: OrderUnitOfMeasure,
    private _material: BasicMaterial,
    private _project: ProjectBasic | null,
    private _site: SiteBasic | null,
    private _customer_account: AccountBasic | null,
    private _company: CompanyBasic,
    private _discarded_at: Dayjs | null = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get buyPrice() {
    return this._buy_price;
  }

  public get sellPrice() {
    return this._sell_price;
  }

  public get unitOfMeasure() {
    return this._unit_of_measure;
  }

  public get material() {
    return this._material;
  }

  public get project() {
    return this._project;
  }

  public get site() {
    return this._site;
  }

  public get customerAccount() {
    return this._customer_account;
  }

  public get company() {
    return this._company;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: MaterialRate_Read) {
    return new MaterialRate(
      proto.id,
      isNil(proto.buy_price) ? null : Number(proto.buy_price),
      isNil(proto.sell_price) ? null : Number(proto.sell_price),
      proto.unit_of_measure,
      BasicMaterial.parse(proto.material),
      isNil(proto.project) ? null : ProjectBasic.parse(proto.project),
      isNil(proto.site) ? null : SiteBasic.parse(proto.site),
      isNil(proto.customer_account) ? null : AccountBasic.parse(proto.customer_account),
      CompanyBasic.parse(proto.company),
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class MaterialRateTypeahead {
  constructor(
    private _id: string,
    private _sell_price: number | null,
    private _buy_price: number | null,
    private _unit_of_measure: OrderUnitOfMeasure | null,
    private _site: SiteTypeahead | null,
    private _material: MaterialTypeahead | null,
    private _project: ProjectTypeahead | null,
    private _customer_account: AccountTypeahead | null,
    private _discarded_at: Dayjs | null = null,
  ) {}

  public get id() {
    return this._id;
  }

  public get sellPrice() {
    return this._sell_price;
  }

  public get buyPrice() {
    return this._buy_price;
  }

  public get unitOfMeasure() {
    return this._unit_of_measure;
  }

  public get site() {
    return this._site;
  }

  public get material() {
    return this._material;
  }

  public get project() {
    return this._project;
  }

  public get customerAccount() {
    return this._customer_account;
  }

  public get discardedAt() {
    return this._discarded_at;
  }

  public static parse(proto: MaterialRate_Read_Typeahead) {
    return new MaterialRateTypeahead(
      proto.id,
      isNil(proto.sell_price) ? null : Number(proto.sell_price),
      isNil(proto.buy_price) ? null : Number(proto.buy_price),
      proto.unit_of_measure,
      isNil(proto.site) ? null : SiteTypeahead.parse(proto.site),
      isNil(proto.material) ? null : MaterialTypeahead.parse(proto.material),
      isNil(proto.project) ? null : ProjectTypeahead.parse(proto.project),
      isNil(proto.customer_account)
        ? null
        : AccountTypeahead.parse(proto.customer_account),
      proto.discarded_at ? dayjs(proto.discarded_at) : null,
    );
  }
}

export class MaterialRateAssignment {
  constructor(
    private _id: string,
    private _buy_price: number | null,
    private _sell_price: number | null,
    private _unit_of_measure: NullableOrderUnitOfMeasure,
    private _site: SiteBasic | null,
  ) {}

  public static parse(proto: MaterialRateAssignment_Read_Nested) {
    return new MaterialRateAssignment(
      proto.id,
      isNil(proto.buy_price) ? null : Number(proto.buy_price),
      isNil(proto.sell_price) ? null : Number(proto.sell_price),
      proto.unit_of_measure,
      isNil(proto.site) ? null : SiteBasic.parse(proto.site),
    );
  }

  public get id() {
    return this._id;
  }

  public get buyPrice() {
    return this._buy_price;
  }

  public get sellPrice() {
    return this._sell_price;
  }

  public get unitOfMeasure() {
    return this._unit_of_measure;
  }

  public get site() {
    return this._site;
  }
}
